import type { ReactNode } from 'react'
import { StatusIllustration, type StatusVariant } from './status-illustration'

type StatusBlockProps = {
  variant: StatusVariant
  title: string
  description: ReactNode
  /**
   * Heading element for the title. Defaults to `h1` — the right choice on an
   * error boundary that has replaced the whole route. The inline empty state on
   * the results page passes `h2`, because the results `<h1>` ("0 Cardiologists
   * in …") is already on the page and there must be exactly one.
   */
  titleAs?: 'h1' | 'h2'
  /** Action buttons/links, rendered in a centred, wrapping row below the copy. */
  children?: ReactNode
}

/**
 * The illustration + heading + copy + actions column, with no page chrome of
 * its own. Used directly for the inline "no results" state; wrapped by
 * StatusScreen for the full-page error boundaries.
 */
export const StatusBlock = ({ variant, title, description, titleAs = 'h1', children }: StatusBlockProps) => {
  const Heading = titleAs
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 text-center">
      <StatusIllustration variant={variant} className="h-32 w-32 sm:h-40 sm:w-40" />
      {/* Explicit font/size classes rather than the bare-tag base styles so an
          h1 and an h2 look identical here — the level is a semantics choice, not
          a visual one. */}
      <Heading className="mt-8 text-balance font-display text-3xl font-medium text-secondary-800 sm:text-4xl">
        {title}
      </Heading>
      <p className="mt-3 text-pretty text-lg text-secondary-600">{description}</p>
      {children != null && <div className="mt-8 flex flex-wrap items-center justify-center gap-3">{children}</div>}
    </div>
  )
}

/**
 * Full-height version for route error boundaries: fills the space between the
 * shared header and footer (the boundary renders inside the page layout's
 * <Outlet />) and vertically centres the block. `grow-1` matches the results
 * and provider pages so the footer sits at the bottom on short content.
 */
export const StatusScreen = (props: StatusBlockProps & { 'data-testid'?: string }) => {
  const { 'data-testid': testId, ...blockProps } = props
  return (
    <main
      className="grow-1 grid place-items-center px-3 py-16 sm:px-4 sm:py-24"
      data-testid={testId ?? 'status-screen'}
    >
      <StatusBlock {...blockProps} />
    </main>
  )
}

/**
 * "Back to home" — a full-navigation anchor rather than a router <Link>. Error
 * boundaries can render when the router or a loader is in a bad state, so a
 * hard navigation is the more reliable escape hatch to a known-good page.
 */
export const BackHomeLink = () => (
  <a href="/" className="btn btn-primary btn-lg" data-testid="status-home">
    Back to home
  </a>
)

/**
 * "Try again" — reloads the current URL, which re-runs the failed loader. Only
 * worth offering for genuine errors (a transient 5xx / network blip may clear);
 * a 404 or an empty result won't change on reload, so those screens omit it.
 */
export const TryAgainButton = () => (
  <button
    type="button"
    onClick={() => {
      window.location.reload()
    }}
    className="btn btn-secondary btn-lg"
    data-testid="status-retry"
  >
    Try again
  </button>
)
